// src/services/rechargeService.ts
// Recarga de billetera por DEPÓSITO / TRANSFERENCIA (República Dominicana).
//
// ANTES: mandaba el comprobante en base64 al backend de Railway, que corría OCR
// con AWS Rekognition. Ese backend está BORRADO — comprobado, 404 en todas sus
// rutas. Así que la entrada de dinero de la plataforma llevaba caída: el
// usuario transfería su dinero de verdad, subía la foto y la app le contestaba
// "No pudimos procesar tu comprobante". Sin recarga no hay membresía.
//
// AHORA: el comprobante se sube a Firebase Storage (`recharge_receipts/{uid}/`)
// y la Cloud Function `submitRechargeReceipt` lo encola para revisión humana —
// el mismo modelo que ya se usa en el KYC. El saldo lo suma el SERVIDOR al
// aprobar (`walletBalance` está bloqueado al cliente en firestore.rules, para
// que nadie se regale dinero).
//
// Ya no hay OCR, así que ya no existe la auto-aprobación instantánea: toda
// recarga pasa por PENDING_REVIEW. Es más lento, pero es verdad — antes lo
// rápido era el mensaje de error.

import { storageService } from './storageService';
import { mensajeDeError } from '../utils/mensajeDeError';
import { pesos } from '../utils/dinero';
import {
  callSubmitRechargeReceipt,
  callResolveRechargeRequest,
  callListPendingRecharges,
} from './cloudFunctions';

export type RechargeStatus =
  | 'APPROVED'
  | 'PENDING_REVIEW'
  | 'REJECTED_DUPLICATE'
  | 'ERROR';

export interface RechargeResult {
  status: RechargeStatus;
  message: string;
  creditedAmount?: number;
  requestId?: string;
}

export const RechargeService = {
  /**
   * Sube el comprobante y lo deja en la cola de revisión.
   *
   * @param uri      URI local de la foto (file://, content:// o data: en web)
   * @param uid      dueño de la recarga — define la carpeta de Storage
   * @param bankId   'POPULAR' | 'BANRESERVAS' (la cuenta a la que transfirió)
   * @param declaredAmount monto que el usuario dice haber depositado
   */
  async submitReceipt(
    uri: string,
    uid: string,
    bankId: string,
    declaredAmount?: number
  ): Promise<RechargeResult> {
    if (!uid) {
      return { status: 'ERROR', message: 'Inicia sesión para recargar tu billetera.' };
    }
    if (!uri) {
      return { status: 'ERROR', message: 'Falta la foto del comprobante.' };
    }

    let receiptUrl: string;
    try {
      // La ruta tiene que empezar por `recharge_receipts/{uid}/` — es lo que
      // validan tanto las reglas de Storage como la Cloud Function.
      receiptUrl = await storageService.uploadToPath(
        `recharge_receipts/${uid}/comprobante_${Date.now()}.jpg`,
        uri
      );
    } catch (e: any) {
      console.warn('[RechargeService] subida del comprobante falló:', e?.code, e?.message);
      return {
        status: 'ERROR',
        message: 'No pudimos subir la foto. Revisa tu conexión e intenta de nuevo.',
      };
    }

    try {
      const r = await callSubmitRechargeReceipt({
        receiptUrl,
        bankId,
        declaredAmount: declaredAmount ?? null,
      });
      return {
        status: r.data.status,
        message: r.data.message,
        requestId: r.data.requestId,
      };
    } catch (e: any) {
      console.warn('[RechargeService] submitRechargeReceipt falló:', e?.code, e?.message);
      return {
        status: 'ERROR',
        // El motivo del servidor ("elige a qué cuenta transferiste", "el
        // comprobante no es tuyo") se respeta; la jerga técnica no llega.
        message: mensajeDeError(e, 'No pudimos enviar tu comprobante. Intenta de nuevo.'),
      };
    }
  },

  /**
   * ADMIN — aprueba o rechaza una recarga. El servidor exige claim de admin
   * con 2FA vigente; el cliente solo llama.
   */
  async adminResolve(
    requestId: string,
    action: 'APPROVE' | 'REJECT',
    amount?: number
  ): Promise<{ ok: boolean; message: string }> {
    try {
      const r = await callResolveRechargeRequest({ requestId, action, amount: amount ?? null });
      if (!r.data?.ok) return { ok: false, message: 'No se pudo procesar.' };
      return {
        ok: true,
        message:
          r.data.status === 'APPROVED'
            ? `Acreditado ${pesos(r.data.credited ?? 0)}.`
            : 'Recarga rechazada.',
      };
    } catch (e: any) {
      console.warn('[RechargeService] adminResolve falló:', e?.code, e?.message);
      return { ok: false, message: mensajeDeError(e, 'No se pudo procesar. Intenta de nuevo.') };
    }
  },

  /** ADMIN — cola de comprobantes esperando revisión, más antiguos primero. */
  async adminListPending() {
    try {
      const r = await callListPendingRecharges();
      return r.data.items ?? [];
    } catch (e: any) {
      console.warn('[RechargeService] listPendingRecharges falló:', e?.code, e?.message);
      return [];
    }
  },
};
